class LetoilePromotionListing {
  constructor(root = document) {
    this.roots = [...root.querySelectorAll('[data-promotion-page][data-promotion-collection-url]')]
      .filter((section) => !section.dataset.promotionRemoteListingBound);

    this.roots.forEach((section) => {
      section.dataset.promotionRemoteListingBound = 'true';
      this.bind(section);
    });
  }

  bind(section) {
    const collectionUrl = new URL(section.dataset.promotionCollectionUrl, window.location.origin);
    const promotionUrl = new URL(window.location.href);
    promotionUrl.search = '';
    let requestController;
    const mobileQuery = window.matchMedia('(max-width: 989px)');

    const syncFacetModalState = (facets) => {
      const isOpen = Boolean(mobileQuery.matches && facets?.open);
      document.body.classList.toggle('le-plp-filter-open', isOpen);
      if (!isOpen) document.body.classList.remove('le-plp-expanded-filter-open');
    };

    const rewriteListingUrl = (value) => {
      const target = new URL(value, window.location.origin);
      if (target.pathname !== collectionUrl.pathname) return value;
      target.pathname = promotionUrl.pathname;
      return `${target.pathname}${target.search}${target.hash}`;
    };

    const prepareRemoteListing = (remotePlp) => {
      const remoteFacets = remotePlp.querySelector('[data-responsive-facets]');
      const remoteResults = remotePlp.querySelector('.le-plp__results');
      if (!remoteResults) return null;

      remoteResults.querySelector('.le-plp__empty')?.remove();
      remoteResults.querySelectorAll('[data-listing-load-more]').forEach((link) => {
        const sourceUrl = new URL(link.getAttribute('href'), window.location.origin);
        link.dataset.listingSourceUrl = `${sourceUrl.pathname}${sourceUrl.search}${sourceUrl.hash}`;
        link.dataset.listingDisplayPathname = promotionUrl.pathname;
      });
      remoteResults.querySelectorAll('a[href]').forEach((link) => {
        link.setAttribute('href', rewriteListingUrl(link.getAttribute('href')));
      });
      remoteResults.querySelectorAll('form[action]').forEach((form) => {
        form.setAttribute('action', promotionUrl.pathname);
      });

      if (remoteFacets) {
        remoteFacets.querySelectorAll('a[href]').forEach((link) => {
          link.setAttribute('href', rewriteListingUrl(link.getAttribute('href')));
        });
        remoteFacets.querySelectorAll('form').forEach((form) => {
          form.setAttribute('action', promotionUrl.pathname);
        });
      }

      return { remoteFacets, remoteResults };
    };

    const applyRemoteListing = ({ remoteFacets, remoteResults }) => {
      const currentBody = section.querySelector('.le-promo__body');
      const currentFacets = section.querySelector('[data-responsive-facets]');
      const currentResults = section.querySelector('.le-plp__results');
      if (!currentBody || !currentResults) return false;

      if (remoteFacets) {
        const importedFacets = document.importNode(remoteFacets, true);
        importedFacets.addEventListener('toggle', () => syncFacetModalState(importedFacets));
        if (currentFacets) {
          currentFacets.replaceWith(importedFacets);
        } else {
          currentBody.prepend(importedFacets);
        }
        currentBody.classList.remove('le-plp__body--no-facets');
        section.classList.remove('le-plp--filters-hidden');
      }

      currentResults.replaceWith(document.importNode(remoteResults, true));
      document.body.classList.remove('le-plp-filter-open', 'le-plp-expanded-filter-open');
      section.removeAttribute('data-le-plp-bound');
      section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      return true;
    };

    const openFacets = (facetKind) => {
      const facets = section.querySelector('[data-responsive-facets]');
      if (!facets) return;

      facets.open = true;
      if (mobileQuery.matches) document.body.classList.add('le-plp-filter-open');

      if (!facetKind) return;
      const facetGroup = [...facets.querySelectorAll('[data-facet-group]')].find((group) => {
        const param = group.dataset.facetParam?.toLocaleLowerCase() || '';
        const label = group.querySelector('[data-facet-label]')?.textContent.toLocaleLowerCase() || '';
        if (facetKind === 'brand') return param.includes('vendor') || label.includes('brand');
        return param.includes('category')
          || param.includes('product_type')
          || label.includes('categor')
          || label.includes('product type')
          || label.includes('assort');
      });

      if (facetGroup) facetGroup.open = true;
      (facetGroup || facets).scrollIntoView({ behavior: 'auto', block: 'start' });
    };

    const closeExpandedFacet = (group) => {
      if (!group) return;
      group.removeAttribute('data-values-expanded');
      section.querySelector('[data-responsive-facets]')?.removeAttribute('data-desktop-expanded');
      document.body.classList.remove('le-plp-expanded-filter-open');
    };

    const loadListing = async (targetUrl, historyMode = 'replace') => {
      requestController?.abort();
      requestController = new AbortController();
      section.setAttribute('aria-busy', 'true');

      const sourceUrl = new URL(collectionUrl);
      sourceUrl.search = targetUrl.search;

      try {
        const response = await fetch(sourceUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: requestController.signal,
        });
        if (!response.ok) return;

        const html = await response.text();
        const remoteDocument = new DOMParser().parseFromString(html, 'text/html');
        const remotePlp = remoteDocument.querySelector('[data-le-plp]');
        const preparedListing = remotePlp ? prepareRemoteListing(remotePlp) : null;
        if (!preparedListing || !applyRemoteListing(preparedListing)) return;

        const nextPromotionUrl = new URL(promotionUrl);
        nextPromotionUrl.search = targetUrl.search;
        if (historyMode === 'push') {
          window.history.pushState({ promotionListing: true }, '', nextPromotionUrl);
        } else if (historyMode === 'replace') {
          window.history.replaceState({ promotionListing: true }, '', nextPromotionUrl);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          section.dispatchEvent(new CustomEvent('letoile:promotion-listing-error', { bubbles: true }));
        }
      } finally {
        if (!requestController.signal.aborted) section.removeAttribute('aria-busy');
      }
    };

    section.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-collection-filter-form], [data-collection-sort-form]');
      if (!form || !section.contains(form)) return;
      event.preventDefault();

      const nextUrl = new URL(promotionUrl);
      const formData = new FormData(form);
      for (const [key, value] of formData) {
        if (String(value).trim() !== '') nextUrl.searchParams.append(key, value);
      }
      loadListing(nextUrl, 'push');
    }, true);

    section.addEventListener('click', (event) => {
      const filterSubmit = event.target.closest('[data-collection-filter-submit]');
      if (filterSubmit && section.contains(filterSubmit)) {
        event.preventDefault();
        filterSubmit.closest('form')?.requestSubmit();
        return;
      }

      const facetTrigger = event.target.closest('[data-le-plp-facet-button]');
      if (facetTrigger && section.contains(facetTrigger)) {
        openFacets();
        return;
      }

      const brandTrigger = event.target.closest('[data-le-plp-brand-button]');
      if (brandTrigger && section.contains(brandTrigger)) {
        openFacets('brand');
        return;
      }

      const categoryTrigger = event.target.closest('[data-le-plp-category-button]');
      if (categoryTrigger && section.contains(categoryTrigger)) {
        openFacets('category');
        return;
      }

      const showAll = event.target.closest('[data-facet-show-all]');
      if (showAll && section.contains(showAll)) {
        const group = showAll.closest('[data-facet-group]');
        group?.setAttribute('data-values-expanded', '');
        document.body.classList.add('le-plp-expanded-filter-open');
        if (!mobileQuery.matches) {
          section.querySelector('[data-responsive-facets]')?.setAttribute('data-desktop-expanded', '');
        }
        group?.querySelector('[data-facet-value-search]')?.focus();
        return;
      }

      const closeExpanded = event.target.closest('[data-facet-expanded-close]');
      if (closeExpanded && section.contains(closeExpanded)) {
        closeExpandedFacet(closeExpanded.closest('[data-facet-group]'));
        return;
      }

      const chooseExpanded = event.target.closest('[data-facet-expanded-choose]');
      if (chooseExpanded && section.contains(chooseExpanded)) {
        const group = chooseExpanded.closest('[data-facet-group]');
        closeExpandedFacet(group);
        if (!mobileQuery.matches) {
          section.querySelector('[data-collection-filter-form]')?.requestSubmit();
        }
        return;
      }

      const showAllFilters = event.target.closest('[data-show-all-filters]');
      if (showAllFilters && section.contains(showAllFilters)) {
        section.querySelectorAll('[data-facet-group]').forEach((group) => {
          group.open = true;
          group.removeAttribute('hidden');
          group.setAttribute('data-values-visible', '');
        });
        return;
      }

      const sortControl = event.target.closest('[data-plp-sort-control]');
      if (sortControl && section.contains(sortControl) && sortControl.value) {
        const sortForm = section.querySelector('[data-collection-sort-form]');
        const sortInput = section.querySelector('[data-plp-sort-input]');
        if (sortForm && sortInput) {
          sortInput.value = sortControl.value;
          sortControl.closest('[data-plp-sort-menu]')?.removeAttribute('open');
          sortForm.requestSubmit();
        }
        return;
      }

      const link = event.target.closest(
        '[data-active-filter-chip], [data-active-filter-clear], .le-pagination a',
      );
      if (!link || !section.contains(link)) return;
      event.preventDefault();
      loadListing(new URL(link.href, window.location.origin), 'push');
    }, true);

    section.addEventListener('change', (event) => {
      const input = event.target.closest('[data-facet-value-input], [data-facet-price-min], [data-facet-price-max]');
      if (!input || !section.contains(input) || mobileQuery.matches) return;
      if (input.closest('[data-facet-group][data-values-expanded]')) return;
      input.closest('form')?.requestSubmit();
    });

    section.addEventListener('input', (event) => {
      const valueSearch = event.target.closest('[data-facet-value-search]');
      if (valueSearch && section.contains(valueSearch)) {
        const query = valueSearch.value.trim().toLocaleLowerCase();
        valueSearch.closest('[data-facet-group]')?.querySelectorAll('[data-facet-value]').forEach((value) => {
          value.hidden = query !== '' && !value.textContent.toLocaleLowerCase().includes(query);
        });
        return;
      }

      const facetSearch = event.target.closest('[data-facet-search]');
      if (!facetSearch || !section.contains(facetSearch)) return;
      const query = facetSearch.value.trim().toLocaleLowerCase();
      const clear = facetSearch.closest('form')?.querySelector('[data-facet-search-clear]');
      if (clear) clear.hidden = query === '';
      section.querySelectorAll('[data-facet-group]').forEach((group) => {
        group.hidden = query !== '' && !group.textContent.toLocaleLowerCase().includes(query);
      });
    });

    window.addEventListener('popstate', () => loadListing(new URL(window.location.href), 'none'));
    loadListing(new URL(window.location.href));
  }
}

new LetoilePromotionListing();
document.addEventListener('shopify:section:load', (event) => new LetoilePromotionListing(event.target));
